import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Activity, 
  MapPin, 
  Globe, 
  Clock, 
  Shield, 
  AlertTriangle,
  CheckCircle2,
  XCircle,
  User,
  Search,
  RefreshCw,
  Trash2,
  Filter,
  Monitor,
  Fingerprint,
  LogIn,
  LogOut
} from 'lucide-react';
import { toast } from 'sonner';

export default function ActivityPanel() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailFilter, setEmailFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [successFilter, setSuccessFilter] = useState('all');
  const [daysFilter, setDaysFilter] = useState(30);

  const token = localStorage.getItem('token');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');
      params.append('days', daysFilter.toString());
      
      if (emailFilter) params.append('user_email_filter', emailFilter);
      if (actionFilter !== 'all') params.append('action_filter', actionFilter);
      if (successFilter !== 'all') params.append('success_filter', successFilter === 'success');

      const response = await axios.get(`${API}/admin/activity-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/activity-logs/stats?days=${daysFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [daysFilter, actionFilter, successFilter]); // Intentionally omitting functions to avoid infinite loops

  const handleSearch = () => {
    fetchLogs();
    fetchStats();
  };

  const handleClearOldLogs = async () => {
    if (!confirm('Are you sure you want to delete activity logs older than 90 days?')) return;
    
    try {
      const response = await axios.delete(`${API}/admin/activity-logs/clear?days_to_keep=90`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchLogs();
      fetchStats();
    } catch (error) {
      toast.error('Failed to clear logs');
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login':
        return <LogIn className="w-4 h-4 text-green-600" />;
      case 'login_biometric':
        return <Fingerprint className="w-4 h-4 text-indigo-600" />;
      case 'logout':
        return <LogOut className="w-4 h-4 text-slate-600" />;
      case 'login_failed':
      case 'login_biometric_failed':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      default:
        return <Activity className="w-4 h-4 text-blue-600" />;
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      'login': 'Password Login',
      'login_biometric': 'Biometric Login',
      'logout': 'Logout',
      'login_failed': 'Failed Login',
      'login_biometric_failed': 'Failed Biometric',
      'admin_test': 'Admin Test',
      'password_reset': 'Password Reset',
      'data_export': 'Data Export',
    };
    return labels[action] || action;
  };

  const getActionBadgeColor = (action, success) => {
    if (!success) return 'bg-red-100 text-red-800 border-red-200';
    
    switch (action) {
      case 'login':
      case 'login_biometric':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'logout':
        return 'bg-slate-100 text-slate-800 border-slate-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatLocation = (log) => {
    const parts = [];
    if (log.geo_city) parts.push(log.geo_city);
    if (log.geo_region) parts.push(log.geo_region);
    if (log.geo_country_code) parts.push(log.geo_country_code);
    return parts.join(', ') || 'Unknown';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-indigo-600" />
            Activity Audit Log
          </h2>
          <p className="text-slate-600 mt-1">Monitor user logins, logouts, and system activity with geolocation</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSearch} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={handleClearOldLogs} className="text-red-600 hover:text-red-700">
            <Trash2 className="w-4 h-4 mr-2" />
            Clear Old Logs
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Activities</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_activities}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Failed Attempts</p>
                  <p className="text-2xl font-bold text-red-600">{stats.failed_activities}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Globe className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Unique IPs</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.unique_ips}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <User className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Unique Users</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.unique_users}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Country Breakdown */}
      {stats?.country_breakdown && stats.country_breakdown.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-indigo-600" />
              Top Locations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.country_breakdown.map((country, idx) => (
                <Badge 
                  key={idx} 
                  variant="outline" 
                  className="px-3 py-1.5 bg-slate-50"
                >
                  <Globe className="w-3.5 h-3.5 mr-1.5" />
                  {country._id}: {country.count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Activity Breakdown */}
      {stats?.user_breakdown && stats.user_breakdown.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-purple-600" />
              Activity by User
            </CardTitle>
            <CardDescription>Activity counts per user in the last {stats.period_days} days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.user_breakdown.map((user, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                  onClick={() => {
                    setEmailFilter(user._id);
                    handleSearch();
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                      <User className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{user._id}</p>
                      <p className="text-xs text-slate-500">
                        Last active: {new Date(user.last_activity).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      {user.success_count}
                    </Badge>
                    {user.fail_count > 0 && (
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        <XCircle className="w-3 h-3 mr-1" />
                        {user.fail_count}
                      </Badge>
                    )}
                    <Badge className="bg-slate-200 text-slate-700">
                      Total: {user.count}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Search by Email</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="user@example.com"
                  value={emailFilter}
                  onChange={(e) => setEmailFilter(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-9"
                />
              </div>
            </div>
            
            <div className="w-[180px]">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Action Type</label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="login">Logins (All)</SelectItem>
                  <SelectItem value="login_biometric">Biometric Only</SelectItem>
                  <SelectItem value="logout">Logouts</SelectItem>
                  <SelectItem value="failed">Failed Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[150px]">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Status</label>
              <Select value={successFilter} onValueChange={setSuccessFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="success">Success Only</SelectItem>
                  <SelectItem value="failed">Failed Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[150px]">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Time Range</label>
              <Select value={daysFilter.toString()} onValueChange={(v) => setDaysFilter(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue placeholder="30 Days" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24 Hours</SelectItem>
                  <SelectItem value="7">Last 7 Days</SelectItem>
                  <SelectItem value="30">Last 30 Days</SelectItem>
                  <SelectItem value="90">Last 90 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Log Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Activity Log</CardTitle>
          <CardDescription>
            Showing {logs.length} activities from the last {daysFilter} days
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Activity className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No activity logs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[160px]">Timestamp</TableHead>
                    <TableHead className="w-[140px]">Action</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="w-[80px]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} className={!log.success ? 'bg-red-50/50' : ''}>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          <span className="text-slate-600">{formatTimestamp(log.timestamp)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={`flex items-center gap-1.5 w-fit ${getActionBadgeColor(log.action, log.success)}`}
                        >
                          {getActionIcon(log.action)}
                          <span className="text-xs">{getActionLabel(log.action)}</span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-3.5 h-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-700">
                            {log.user_email || 'Unknown'}
                          </span>
                        </div>
                        {log.action_detail && (
                          <p className="text-xs text-slate-500 mt-0.5 ml-5">{log.action_detail}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Monitor className="w-3.5 h-3.5 text-slate-400" />
                          <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                            {log.ip_address || 'N/A'}
                          </code>
                        </div>
                        {log.geo_isp && (
                          <p className="text-xs text-slate-500 mt-0.5 ml-5">{log.geo_isp}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-slate-400" />
                          <span className="text-sm text-slate-600">{formatLocation(log)}</span>
                        </div>
                        {log.geo_lat && log.geo_lon && (
                          <p className="text-xs text-slate-400 mt-0.5 ml-5">
                            {log.geo_lat.toFixed(2)}, {log.geo_lon.toFixed(2)}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        {log.success ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
